/**
 * useProgress - Hook to track learning progress
 */

import { useState, useEffect, useCallback } from 'react';
import { progressService } from '../services/ProgressService';
import { storageService } from '../services/StorageService';

export function useProgress() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshProgress = useCallback(() => {
    const overallProgress = progressService.getOverallProgress();
    setProgress(overallProgress);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  const completeLesson = useCallback((lessonId, options = {}) => {
    const result = progressService.completeLesson(lessonId, options);
    refreshProgress();
    return result;
  }, [refreshProgress]);

  const completeChallenge = useCallback((challengeId, options = {}) => {
    const result = progressService.completeChallenge(challengeId, options);
    refreshProgress();
    return result;
  }, [refreshProgress]);

  const completeQuiz = useCallback((quizId, score, total) => {
    const result = progressService.completeQuiz(quizId, score, total);
    refreshProgress();
    return result;
  }, [refreshProgress]);

  const saveLessonProgress = useCallback((lessonId, step) => {
    progressService.saveLessonProgress(lessonId, step);
  }, []);

  // Function to get the current progress object
  const getProgress = useCallback(() => {
    return progress || {
      totalPoints: 0,
      lessons: { completed: 0, total: 0 },
      lessonProgress: {}
    };
  }, [progress]);

  // Alias for completeLesson (for backwards compatibility)
  const markLessonComplete = useCallback((lessonId) => {
    return completeLesson(lessonId);
  }, [completeLesson]);

  // Update lesson progress by percentage
  const updateLessonProgress = useCallback((lessonId, percentage) => {
    progressService.saveLessonProgress(lessonId, Math.round(percentage));
  }, []);

  // Add points to total
  const addPoints = useCallback((points) => {
    progressService.addPoints(points);
    refreshProgress();
  }, [refreshProgress]);

  return {
    progress,
    loading,
    refreshProgress,
    
    // Actions
    completeLesson,
    completeChallenge,
    completeQuiz,
    saveLessonProgress,
    markLessonComplete,
    updateLessonProgress,
    addPoints,
    
    // Quick accessors
    totalPoints: progress?.totalPoints || 0,
    currentLevel: progress?.currentLevel || { level: 1, name: 'Beginner' },
    lessonsCompleted: progress?.lessons?.completed || 0,
    lessonsTotal: progress?.lessons?.total || 0,
    lessonsPercentage: progress?.lessons?.percentage || 0,
    challengesCompleted: progress?.challenges?.completed || 0,
    
    // Check methods
    isLessonCompleted: (id) => storageService.isLessonCompleted(id),
    isChallengeCompleted: (id) => storageService.isChallengeCompleted(id),
    getLessonProgress: (id) => storageService.getLessonProgress(id),
    getProgress, // Add getProgress function for accessing progress object
    
    // Daily goal
    dailyGoal: progressService.checkDailyGoal(),
    
    // Category progress
    categoryProgress: progressService.getProgressByCategory()
  };
}

export default useProgress;